
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Validate } from '@/types';
import PromptLocaleEditor from '@/components/rally-intelligence/PromptLocaleEditor.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import '@/validation-rules';

@Component<PromptEditorModal>({
	components: {
		PromptLocaleEditor,
		TextButton,
		TextField,
		ValidationObserver,
		ValidationProvider
	}
})
export default class PromptEditorModal extends Vue {
	@Prop({ type: Object, default: null }) private prompt!: RallyIntelligencePromptResponse | null;
	@Prop({ type: Boolean, default: false }) private saving!: boolean;
	@Prop({ type: Object, default: null }) private selectedLocale!: RestaurantLocale | null;

	$refs!: { observer: Validate; };

	private draft: RallyIntelligencePromptRequest = {
		name: this.prompt?.name ?? '',
		description: this.prompt?.description ?? '',
		content: this.prompt?.content ?? '',
		bot: 'catering_bot',
		localization: this.prompt?.localization ? JSON.parse(JSON.stringify(this.prompt.localization)) : undefined
	};

	private async save(): Promise<void> {
		const isValid = await this.$refs.observer.validate();
		if (!isValid) {
			return;
		}
		this.$emit('save', this.buildDraft());
	}

	private async saveAndTest(): Promise<void> {
		const isValid = await this.$refs.observer.validate();
		if (!isValid) {
			return;
		}
		this.$emit('save-and-test', this.buildDraft());
	}

	private buildDraft(): RallyIntelligencePromptRequest {
		const localization = this.cleanLocalization(this.draft.localization);
		return {
			name: this.draft.name.trim(),
			description: this.draft.description.trim(),
			content: this.draft.content,
			bot: 'catering_bot',
			...(localization ? { localization } : {})
		};
	}

	/**
	 * Strip empty locale entries out of the localization map before sending it
	 * to the server. A locale is considered empty when its name, description,
	 * and content are all blank (or whitespace only). If every locale ends up
	 * empty, returns `undefined` so the field is omitted from the request entirely.
	 *
	 * @param {{ [key: string]: LocalizationInfo } | undefined} localization
	 * @return {{ [key: string]: LocalizationInfo } | undefined}
	 */
	private cleanLocalization(localization: { [key: string]: LocalizationInfo } | undefined): { [key: string]: LocalizationInfo } | undefined {
		if (!localization) {
			return undefined;
		}
		const result: { [key: string]: LocalizationInfo } = {};
		Object.keys(localization).forEach(key => {
			const entry = localization[key];
			if (!entry) {
				return;
			}
			// content isn't trimmed — it's rich-text where whitespace can be intentional
			const trimmedName = entry.name?.trim() ?? '';
			const trimmedDescription = entry.description?.trim() ?? '';
			const trimmedContent = entry.content ?? '';
			if (!trimmedName && !trimmedDescription && !trimmedContent) {
				return;
			}
			result[key] = {
				name: trimmedName,
				description: trimmedDescription,
				content: trimmedContent
			};
		});
		// Return undefined (not `{}`) so the field is omitted from the request
		return Object.keys(result).length ? result : undefined;
	}
}
